import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaCopy,
  FaSearch,
  FaShare,
  FaSpinner,
  FaTimes,
  FaUser,
  FaUserFriends,
  FaExclamationTriangle,
} from "react-icons/fa";
import Modal from "../common/Modal";
import api from "../../utils/api/axiosInstance";
import getImageUrl from "../../utils/imageUtils";
import { useUserAuth } from "../../contexts/UserAuthContext";
import { useToast } from "../../contexts/ToastContext";
import styles from "../../styles/components/profile/SharePostModal.module.css";

const computeMutualFriends = (following, followers) => {
  const followerIds = new Set(
    followers.map((f) => f._id?.toString() || f._id),
  );
  return following.filter((f) =>
    followerIds.has(f._id?.toString() || f._id),
  );
};

const buildPostUrl = (postId) => `${window.location.origin}/post/${postId}`;

const buildShareText = (postUrl, note) => {
  const lines = [];
  if (note?.trim()) {
    lines.push(note.trim());
  }
  lines.push(postUrl);
  return lines.join("\n\n");
};

const SharePostModal = ({ isOpen, onClose, post }) => {
  const { user: currentUser } = useUserAuth();
  const { showToast } = useToast();
  const [friends, setFriends] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [friendsError, setFriendsError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [copying, setCopying] = useState(false);
  const [externalSharing, setExternalSharing] = useState(false);

  const postUrl = post?._id ? buildPostUrl(post._id) : "";
  const visibility = post?.visibility || "public";
  const isPrivate = visibility === "private";
  const isFollowersOnly = visibility === "followers";

  const resetState = useCallback(() => {
    setFriends([]);
    setFriendsError(null);
    setSearch("");
    setSelectedIds(new Set());
    setNote("");
    setSending(false);
    setCopying(false);
    setExternalSharing(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  useEffect(() => {
    if (!isOpen || !currentUser || !post?._id) return;

    let cancelled = false;

    const loadFriends = async () => {
      try {
        setLoadingFriends(true);
        setFriendsError(null);
        const [followingRes, followersRes] = await Promise.all([
          api.get("/users/me/following"),
          api.get("/users/me/followers"),
        ]);
        if (cancelled) return;
        const following = followingRes.data.following || [];
        const followers = followersRes.data.followers || [];
        setFriends(computeMutualFriends(following, followers));
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading friends:", err);
        setFriendsError(
          err.response?.data?.message || "Failed to load friends",
        );
      } finally {
        if (!cancelled) {
          setLoadingFriends(false);
        }
      }
    };

    loadFriends();

    return () => {
      cancelled = true;
    };
  }, [isOpen, currentUser, post?._id]);

  const filteredFriends = useMemo(() => {
    if (!search.trim()) return friends;
    const query = search.toLowerCase();
    return friends.filter((friend) => {
      const fullName = `${friend.firstName || ""} ${friend.lastName || ""}`
        .trim()
        .toLowerCase();
      const username = friend.username?.toLowerCase() || "";
      return fullName.includes(query) || username.includes(query);
    });
  }, [friends, search]);

  const trackShareEngagement = async () => {
    try {
      await api.post(`/posts/${post._id}/share`);
    } catch (err) {
      console.debug("Share tracking failed:", err);
    }
  };

  const toggleFriend = (friendId) => {
    const id = friendId?.toString() || friendId;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCopyLink = async () => {
    if (!postUrl) return;
    try {
      setCopying(true);
      await navigator.clipboard.writeText(postUrl);
      await trackShareEngagement();
      showToast({
        message: "Post link copied to clipboard!",
        type: "success",
      });
    } catch (err) {
      console.error("Error copying link:", err);
      showToast({
        message: `Share this link: ${postUrl}`,
        type: "info",
      });
    } finally {
      setCopying(false);
    }
  };

  const handleExternalShare = async () => {
    if (!post || !postUrl) return;

    const shareData = {
      title: `Post by ${
        post.user
          ? `${post.user.firstName} ${post.user.lastName}`
          : "[Deleted User]"
      }`,
      text:
        post.caption ||
        `Check out this post about ${
          post.products?.map((p) => p.name).join(", ") || "products"
        }`,
      url: postUrl,
    };

    try {
      setExternalSharing(true);
      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare(shareData)
      ) {
        await navigator.share(shareData);
        await trackShareEngagement();
        handleClose();
        return;
      }
      await handleCopyLink();
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Error sharing post:", err);
        showToast({ message: "Failed to share post", type: "error" });
      }
    } finally {
      setExternalSharing(false);
    }
  };

  const handleSendToFriends = async () => {
    if (!currentUser) {
      showToast({ message: "Please log in to share with friends", type: "error" });
      return;
    }
    if (isPrivate) {
      showToast({
        message: "Private posts cannot be shared",
        type: "error",
      });
      return;
    }
    if (selectedIds.size === 0) {
      showToast({ message: "Select at least one friend", type: "error" });
      return;
    }

    const text = buildShareText(postUrl, note);
    const recipientIds = Array.from(selectedIds);

    try {
      setSending(true);
      const results = await Promise.allSettled(
        recipientIds.map((recipientId) =>
          api.post("/messages/send", { recipientId, text }),
        ),
      );

      const successCount = results.filter(
        (result) => result.status === "fulfilled",
      ).length;
      const failureCount = recipientIds.length - successCount;

      if (successCount > 0) {
        await trackShareEngagement();
      }

      if (failureCount === 0) {
        showToast({
          message:
            successCount === 1
              ? "Post sent to your friend"
              : `Post sent to ${successCount} friends`,
          type: "success",
        });
        handleClose();
      } else if (successCount > 0) {
        showToast({
          message: `Sent to ${successCount} friend${successCount === 1 ? "" : "s"}, but ${failureCount} failed`,
          type: "info",
        });
      } else {
        showToast({
          message: "Failed to send post to friends",
          type: "error",
        });
      }
    } catch (err) {
      console.error("Error sending post to friends:", err);
      showToast({
        message: err.response?.data?.message || "Failed to send post",
        type: "error",
      });
    } finally {
      setSending(false);
    }
  };

  const canUseNativeShare =
    typeof navigator !== "undefined" &&
    navigator.share &&
    navigator.canShare &&
    navigator.canShare({ url: postUrl });

  if (!post) return null;

  const showFriendsPicker = Boolean(currentUser) && !isPrivate;
  const hasFriends = friends.length > 0;
  const showSearch = showFriendsPicker && (loadingFriends || hasFriends);
  const showNote = showFriendsPicker && hasFriends;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className={styles.shareModal}
      contentClassName={styles.modalContent}
      hideDefaultClose
      ariaLabelledBy="share-post-modal-title"
    >
      <header className={styles.header}>
        <h2 id="share-post-modal-title" className={styles.title}>
          Share post
        </h2>
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Close dialog"
        >
          <FaTimes aria-hidden="true" />
        </button>
      </header>

      <div className={styles.body}>
        {isPrivate && (
          <div className={styles.visibilityWarning} role="alert">
            <FaExclamationTriangle className={styles.bannerIcon} />
            <p>Private posts can only be viewed by you and cannot be shared.</p>
          </div>
        )}

        {isFollowersOnly && !isPrivate && (
          <div className={styles.visibilityNotice} role="status">
            <FaExclamationTriangle className={styles.bannerIcon} />
            <p>
              This post is followers-only. Friends who do not follow{" "}
              {post.user?.firstName || "the author"} may not be able to view it.
            </p>
          </div>
        )}

        {!currentUser && (
          <div className={styles.loginPrompt}>
            <p>Log in to send this post to mutual followers.</p>
            <Link to="/login" className={styles.loginLink} onClick={handleClose}>
              Log in
            </Link>
          </div>
        )}

        {showFriendsPicker && (
          <section className={styles.section} aria-labelledby="share-friends-heading">
            <div className={styles.sectionHeader}>
              <div>
                <h3 id="share-friends-heading" className={styles.sectionTitle}>
                  <FaUserFriends aria-hidden="true" />
                  Send to friends
                </h3>
                <p className={styles.sectionHint}>
                  Mutual followers — people you follow who follow you back.
                </p>
              </div>
              {selectedIds.size > 0 && (
                <span className={styles.selectedBadge}>
                  {selectedIds.size} selected
                </span>
              )}
            </div>

            {showSearch && (
              <div className={styles.searchWrapper}>
                <FaSearch className={styles.searchIcon} aria-hidden="true" />
                <input
                  type="search"
                  className={styles.searchInput}
                  placeholder="Search friends"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  disabled={loadingFriends}
                  aria-label="Search friends"
                />
              </div>
            )}

            <div className={styles.friendsPanel}>
              {loadingFriends && (
                <div className={styles.panelState}>
                  <FaSpinner className={styles.spinner} aria-hidden="true" />
                  <span>Loading friends…</span>
                </div>
              )}

              {!loadingFriends && friendsError && (
                <div className={styles.panelState}>
                  <p>{friendsError}</p>
                </div>
              )}

              {!loadingFriends && !friendsError && !hasFriends && (
                <div className={styles.panelState}>
                  <FaUserFriends className={styles.emptyIcon} aria-hidden="true" />
                  <p>No friends yet</p>
                  <span>
                    Follow someone who follows you back to send posts directly.
                  </span>
                </div>
              )}

              {!loadingFriends &&
                !friendsError &&
                hasFriends &&
                filteredFriends.length === 0 && (
                  <div className={styles.panelState}>
                    <p>No friends match “{search.trim()}”</p>
                  </div>
                )}

              {!loadingFriends &&
                filteredFriends.map((friend) => {
                  const friendId = friend._id?.toString() || friend._id;
                  const isSelected = selectedIds.has(friendId);
                  return (
                    <label
                      key={friendId}
                      className={`${styles.friendRow} ${
                        isSelected ? styles.friendRowSelected : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleFriend(friendId)}
                        disabled={sending}
                        className={styles.friendCheckbox}
                      />
                      <div className={styles.friendAvatar}>
                        {friend.avatar ? (
                          <img src={getImageUrl(friend.avatar)} alt="" />
                        ) : (
                          <FaUser aria-hidden="true" />
                        )}
                      </div>
                      <div className={styles.friendInfo}>
                        <span className={styles.friendName}>
                          {friend.firstName} {friend.lastName}
                        </span>
                        {friend.username && (
                          <span className={styles.friendUsername}>
                            @{friend.username}
                          </span>
                        )}
                      </div>
                    </label>
                  );
                })}
            </div>
          </section>
        )}

        {showNote && (
          <section className={styles.section}>
            <label className={styles.fieldLabel} htmlFor="share-post-note">
              Note
              <span className={styles.optional}>optional</span>
            </label>
            <textarea
              id="share-post-note"
              className={styles.noteInput}
              placeholder="Add a message with the link…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={500}
              disabled={sending}
            />
          </section>
        )}

        {!isPrivate && (
          <section className={styles.section} aria-labelledby="share-link-heading">
            <div className={styles.linkDivider}>
              <span id="share-link-heading">Or copy link</span>
            </div>
            <div className={styles.linkRow}>
              <input
                id="share-post-link"
                type="text"
                readOnly
                value={postUrl}
                className={styles.linkInput}
                aria-label="Post link"
              />
              <button
                type="button"
                className={styles.copyButton}
                onClick={handleCopyLink}
                disabled={copying}
              >
                <FaCopy aria-hidden="true" />
                {copying ? "Copying…" : "Copy"}
              </button>
            </div>
          </section>
        )}
      </div>

      <footer className={styles.footer}>
        {canUseNativeShare && !isPrivate && (
          <button
            type="button"
            className={styles.textButton}
            onClick={handleExternalShare}
            disabled={externalSharing}
          >
            <FaShare aria-hidden="true" />
            {externalSharing ? "Sharing…" : "Share via device"}
          </button>
        )}
        <div className={styles.footerActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={handleClose}
            disabled={sending}
          >
            Cancel
          </button>
          {currentUser && !isPrivate && (
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleSendToFriends}
              disabled={sending || loadingFriends || selectedIds.size === 0}
            >
              {sending ? (
                <>
                  <FaSpinner className={styles.spinner} aria-hidden="true" />
                  Sending…
                </>
              ) : selectedIds.size > 0 ? (
                `Send (${selectedIds.size})`
              ) : (
                "Send"
              )}
            </button>
          )}
        </div>
      </footer>
    </Modal>
  );
};

export default SharePostModal;
